<script setup>
import { ref, watch, computed } from 'vue'
import InlineModal from '@/Components/App/InlineModal.vue'
import { util } from '@/Helpers'
import TextInput from '@/Components/Forms/TextInput.vue'
import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/20/solid'
const props = defineProps({
    show: { type: Boolean, default: false },
    contact: { type: Object, default: null },
    loading: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'save'])

const localContact = ref(null)
const emailValid = ref(false)
const sendMarketing = ref(false)

watch(() => props.contact, (val) => {
    if (!val) {
        localContact.value = null
        emailValid.value = false
        return
    }
    // shallow copy
    localContact.value = JSON.parse(JSON.stringify(val))
    const email = (localContact.value.emails && localContact.value.emails[0]) || ''
    emailValid.value = util.isValidEmail(email)
    // initialize sendMarketing flag from marketable_contact map if available
    try {
        sendMarketing.value = !!(localContact.value.marketable_contact && localContact.value.emails && localContact.value.emails[0] && localContact.value.marketable_contact[localContact.value.emails[0]] && localContact.value.marketable_contact[localContact.value.emails[0]].send_marketing)
    } catch (e) {
        sendMarketing.value = false
    }
}, { immediate: true })

const primaryEmail = computed({
    get() {
        return (localContact.value && localContact.value.emails && localContact.value.emails[0]) || ''
    },
    set(v) {
        if (!localContact.value) return
        localContact.value.emails = [v]
        emailValid.value = util.isValidEmail(v)
        try {
            sendMarketing.value = !!(localContact.value.marketable_contact && localContact.value.marketable_contact[v] && localContact.value.marketable_contact[v].send_marketing)
        } catch (e) {
            sendMarketing.value = false
        }
    }
})

// email binding handled by `primaryEmail` computed

function close() {
    emit('close', localContact.value)
}

function save() {
    if (!emailValid.value) return
    // ensure marketable_contact map updated for the email
    const email = (localContact.value.emails && localContact.value.emails[0]) || null
    if (email) {
        const existing = { ...(localContact.value.marketable_contact || {}) }
        existing[email] = { ...(existing[email] || {}), send_marketing: !!sendMarketing.value }
        localContact.value.marketable_contact = existing
    }

    emit('save', localContact.value)
}
</script>

<template>
    <InlineModal :show="props.show" maxWidth="xl" :padding-enable="false" @close="close">
        <div v-if="props.show" class="p-6">
            <div class="flex items-start gap-4">
                <div class="text-lifeworx-red-600">
                    <ExclamationTriangleIcon class="h-6 w-6" />
                </div>
                <div class="flex-1">
                    <h3 class="text-md font-semibold text-stone-800">Missing Valid Email Address</h3>
                    <p class="mt-1 text-xs text-stone-500">The selected contact is missing a valid email address. Please update the contact record now.</p>

                    <div class="mt-6 grid grid-cols-12 gap-4 items-center">
                        <div class="col-span-4">
                            <div class="text-xs font-semibold text-stone-500">Name</div>
                            <div class="mt-1 text-sm text-lifeworx-blue-800 font-semibold">{{ localContact?.first_name || '—' }} {{ localContact?.last_name || '' }}</div>
                        </div>
                        <div class="col-span-1">
                            <div class="text-xs font-semibold text-stone-500">Type</div>
                            <div class="mt-1 text-sm text-lifeworx-blue-800 font-semibold">{{ localContact?.type || '—' }}</div>
                        </div>
                        <div class="col-span-4 text-right">
                            <div class="text-xs font-semibold text-stone-500">Primary Contact</div>
                            <div class="mt-1">
                                <LwxSwitch v-model="localContact.primary_contact" />
                            </div>
                        </div>
                        <div class="col-span-3 text-right">
                            <div class="text-xs font-semibold text-stone-500">Billing Contact</div>
                            <div class="mt-1">
                                <LwxSwitch v-model="localContact.billing_contact" />
                            </div>
                        </div>
                    </div>

                    <div class="mt-6 grid grid-cols-12 gap-4 items-center">
                        <div class="col-span-8">
                            <label class="text-xs font-semibold text-stone-500">Primary Email</label>
                            <div class="mt-1 relative">
                                <TextInput
                                    id="update-contact-primary-email"
                                    type="email"
                                    :validate="['nullable', 'email']"
                                    v-model="primaryEmail"
                                    :input_class="emailValid ? 'w-full px-2.5 py-1.5 text-sm' : 'w-full pl-6 pr-2.5 py-1.5 text-sm'"
                                />
                                <span v-if="!emailValid" class="absolute left-3 top-3 h-2.5 w-2.5 bg-lifeworx-red-600 rounded-full"></span>
                            </div>
                        </div>
                        <div class="col-span-4">
                            <div class="text-right">
                                <div class="text-xs font-semibold text-stone-500">Send Marketing</div>
                                <div class="mt-1">
                                    <LwxSwitch v-model="sendMarketing" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-8 flex justify-end gap-4">
                <button class="rounded-lg border px-6 py-3 text-sm" @click="close">Cancel</button>
                <button
                    class="rounded-lg px-6 py-3 text-sm text-white bg-lifeworx-blue-700"
                    :class="{ 'opacity-50 cursor-not-allowed': !emailValid || props.loading }"
                    :disabled="!emailValid || props.loading"
                    @click="save"
                >
                    <template v-if="props.loading">Saving...</template>
                    <template v-else>Save</template>
                </button>
            </div>
        </div>
    </InlineModal>
</template>
